import { useState, useEffect } from "react";
import { Form, Select, Input, Switch, FormInstance } from "antd";
import { LanguageDto } from "../../types/languagetypes";
import { Currency } from "../../types/currencytypes";
import { getMenuTraits } from "../../services/pageservice";

interface TraitOption {
  value: string;
  label: string;
}

interface StoreDetailsFormProps {
  form: FormInstance;
  activeLanguages: LanguageDto[];
  activeCurrencies: Currency[];
}

const StoreDetailsForm: React.FC<StoreDetailsFormProps> = ({
  form,
  activeLanguages,
  activeCurrencies,
}) => {
  const [traits, setTraits] = useState<TraitOption[]>([]);

  useEffect(() => {
    const fetchAllMenuTraits = async () => {
      try {
        const data = await getMenuTraits();
        const traitsData: TraitOption[] = data.map((item: any) => ({
          value: item.value,
          label: item.label,
        }));
        setTraits(traitsData);
      } catch (error) {
        console.error("Error fetching menu traits.", error);
      }
    };

    fetchAllMenuTraits();
  }, []);


  const supportedLanguages =
    Form.useWatch("supportedLanguages", form) || [];

  useEffect(() => {
    const primary = form.getFieldValue("primaryLanguage");

    if (
      (!supportedLanguages.length && primary) ||
      (primary && !supportedLanguages.includes(primary))
    ) {
      form.setFieldsValue({ primaryLanguage: null });
    }
  }, [supportedLanguages, form]);

  return (
    <Form form={form} colon layout="horizontal">

      <Form.Item
        label="Traits"
        name="traits"
        labelCol={{ span: 6 }}
        wrapperCol={{ span: 18 }}
      >
        <Select
          mode="multiple"
          options={traits}
          placeholder="Select your traits"
        />
      </Form.Item>

      <Form.Item
        label="Allow Out of Stock"
        name="allowOutOfStock"
        valuePropName="checked"
        labelCol={{ span: 6 }}
        wrapperCol={{ span: 18 }}
      >
        <Switch size="small" />
      </Form.Item>

      <Form.Item
        label="Restock Time"
        name="restockTime"
        rules={[{ required: true, message: "Please enter restock time." }]}
        labelCol={{ span: 6 }}
        wrapperCol={{ span: 18 }}
      >
        <Input />
      </Form.Item>

      <Form.Item
        label="Kiosks"
        name="kiosks"
        rules={[{ required: true, message: "Please enter number of kiosks." }]}
        labelCol={{ span: 6 }}
        wrapperCol={{ span: 18 }}
      >
        <Input />
      </Form.Item>

      <Form.Item
        label="Supported Languages"
        name="supportedLanguages"
        required
        rules={[
          {
            validator: (_, value) => {
              if (!value || value.length === 0) {
                return Promise.reject(
                  new Error("Select at least one supported language")
                );
              }
              return Promise.resolve();
            },
          },
        ]}
        labelCol={{ span: 6 }}
        wrapperCol={{ span: 18 }}
      >
        <Select
          mode="multiple"
          placeholder="Select supported languages"
          options={activeLanguages.map((l) => ({
            label: `${l.name} (${l.iso639_1})`,
            value: l.guid,
          }))}
        />
      </Form.Item>

      <Form.Item
        label="Primary Language"
        required
        dependencies={["supportedLanguages"]}
        labelCol={{ span: 6 }}
        wrapperCol={{ span: 18 }}
      >
        {({ getFieldValue }) => {
          const supported: string[] =
            getFieldValue("supportedLanguages") || [];

          const options = activeLanguages
            .filter(l => supported.includes(l.guid))
            .map(l => ({
              label: `${l.name} (${l.iso639_1})`,
              value: l.guid,
            }));

          return (
            <Form.Item
              name="primaryLanguage"
              noStyle
              rules={[
                {
                  validator: (_, value) => {
                    if (supported.length > 0 && !value) {
                      return Promise.reject(
                        new Error("Primary language is required")
                      );
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Select
                allowClear
                disabled={supported.length === 0}
                placeholder={
                  supported.length === 0
                    ? "Select supported languages first"
                    : "Select primary language"
                }
                options={options}
              />
            </Form.Item>
          );
        }}
      </Form.Item>
      <Form.Item
        label="Currency"
        name="currency"
        rules={[
          { required: true, message: "Please select a currency" }
        ]}
        labelCol={{ span: 6 }}
        wrapperCol={{ span: 18 }}
      >
        <Select
          placeholder="Select currency"
          options={activeCurrencies.map(c => ({
            label: `${c.currencyName} (${c.currencyCode})`,
            value: c.guid
          }))}
        />
      </Form.Item>
    </Form>
  );
};

export default StoreDetailsForm;